import React, { useState } from 'react';
import { Coins, TrendingUp, AlertCircle, CheckCircle, Mic, Users, Calendar } from 'lucide-react';

export default function MyRewardsTab({ balance, transactions = [], tokenPrice = 0.40, closestMilestone, weeklyStreakThreshold = 5, weeklyStreakBonus = 0.5, minWithdrawalUSD = 100 }) {
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');

  const availableBalance = balance?.availableBalance || 0;
  const pendingBalance = balance?.pendingBalance || 0;
  const usdValue = availableBalance * tokenPrice;
  const canWithdraw = usdValue >= minWithdrawalUSD;
  const progressToWithdrawal = Math.min((usdValue / minWithdrawalUSD) * 100, 100);
  const neededUSD = Math.max(minWithdrawalUSD - usdValue, 0);

  // Weekly summary from live balance
  const weeklyHosts = balance?.weeklyHostCount || 0;
  const weeklyInvites = balance?.weeklyInviteCount || 0;
  const weeklyAttends = balance?.weeklyAttendCount || 0;
  const weeklyTotal = balance?.weeklyTokensEarned || (weeklyHosts * 1.0 + weeklyInvites * 0.25 + weeklyAttends * 0.10);
  const weeklyStreakClaimed = balance?.weeklyStreakClaimed || false;

  const needsMoreForStreak = weeklyStreakClaimed ? 0 : Math.max(weeklyStreakThreshold - weeklyHosts, 0);

  const getActionIcon = (type) => {
    switch (type) {
      case 'host': return <Mic className="w-4 h-4 text-[#8b5cf6]" />;
      case 'invite': return <Users className="w-4 h-4 text-[#00c853]" />;
      case 'attend': return <Calendar className="w-4 h-4 text-[#3b82f6]" />;
      default: return <Coins className="w-4 h-4 text-gray-400" />;
    }
  };

  const getActionLabel = (transaction) => {
    switch (transaction.actionType) {
      case 'host': return 'Hosted event';
      case 'invite': return 'Invitee joined';
      case 'attend': return 'Attended event';
      case 'bonus': return 'Bonus earned';
      case 'signup': return 'Welcome bonus';
      default: return transaction.actionType;
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="p-6 space-y-6 max-h-[600px] overflow-y-auto">
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-[#8b5cf6]/20 to-[#00c853]/10 border border-white/10 rounded-xl">
        <div className="p-6">
          <div className="text-center space-y-2">
            <p className="text-gray-400 text-sm">Your Digital Rewards Balance</p>
            <div className="flex items-center justify-center gap-2">
              <Coins className="w-8 h-8 text-[#8b5cf6]" />
              <span className="text-4xl font-bold text-white">{availableBalance.toFixed(2)}</span>
            </div>
            <p className="text-gray-300 text-lg">&asymp; ${usdValue.toFixed(2)} USD</p>
            {pendingBalance > 0 && (
              <p className="text-xs text-yellow-400">+ {pendingBalance.toFixed(2)} pending</p>
            )}
          </div>
        </div>
      </div>

      {/* Withdrawal Section */}
      <div className="bg-white/5 border border-white/10 rounded-xl">
        <div className="p-6 space-y-4">
          {!canWithdraw ? (
            <>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-white font-medium">Minimum Withdrawal: ${minWithdrawalUSD}</p>
                  <p className="text-gray-400 text-sm mt-1">Your balance: ${usdValue.toFixed(2)}</p>
                  <p className="text-gray-400 text-sm">You need: ${neededUSD.toFixed(2)} more to withdraw</p>
                </div>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#8b5cf6] rounded-full transition-all"
                  style={{ width: `${progressToWithdrawal}%` }}
                />
              </div>
              <p className="text-xs text-gray-400">
                Host events and invite people to reach your withdrawal threshold faster.
              </p>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-white font-medium">You're eligible to withdraw!</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Available: ${usdValue.toFixed(2)} ({availableBalance.toFixed(2)} rewards)
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-300 block mb-1">Amount to withdraw (rewards)</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="0.00"
                      className="flex-1 bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors"
                    />
                    <button
                      onClick={() => setWithdrawAmount(availableBalance.toString())}
                      className="px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors text-sm"
                    >
                      MAX
                    </button>
                  </div>
                  {withdrawAmount && (
                    <p className="text-xs text-gray-400 mt-1">
                      &asymp; ${(parseFloat(withdrawAmount) * tokenPrice).toFixed(2)} USD
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm text-gray-300 block mb-1">Wallet address</label>
                  <input
                    type="text"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="Enter your wallet address"
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors"
                  />
                </div>

                <button
                  className="w-full py-2 px-4 bg-[#8b5cf6] hover:bg-[#8b5cf6]/80 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Request Withdrawal
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Withdrawals typically process within 5-7 business days.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Active Opportunities */}
      {(needsMoreForStreak > 0 || closestMilestone) && (
        <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-xl">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-yellow-400" />
              <h3 className="text-white font-semibold">Active Opportunities</h3>
            </div>
            <div className="space-y-3">
              {needsMoreForStreak > 0 && weeklyHosts < weeklyStreakThreshold && (
                <div className="p-3 bg-white/5 rounded-lg border border-yellow-500/20">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">&#128293;</span>
                    <div className="flex-1">
                      <p className="text-white font-medium">Weekly Streak Bonus</p>
                      <p className="text-gray-400 text-sm mt-1">
                        Host {needsMoreForStreak} more event{needsMoreForStreak > 1 ? 's' : ''} this week to earn +{weeklyStreakBonus} rewards!
                      </p>
                      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mt-2">
                        <div
                          className="h-full bg-yellow-400 rounded-full transition-all"
                          style={{ width: `${(weeklyHosts / weeklyStreakThreshold) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{weeklyHosts} / {weeklyStreakThreshold} events</p>
                    </div>
                  </div>
                </div>
              )}

              {closestMilestone && (
                <div className="p-3 bg-white/5 rounded-lg border border-yellow-500/20">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">&#127919;</span>
                    <div className="flex-1">
                      <p className="text-white font-medium">Next Milestone</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {closestMilestone.remaining} more {closestMilestone.type.replace(/_/g, ' ')} to unlock +{closestMilestone.bonus} rewards!
                      </p>
                      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mt-2">
                        <div
                          className="h-full bg-yellow-400 rounded-full transition-all"
                          style={{ width: `${(closestMilestone.current / closestMilestone.threshold) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {closestMilestone.current} / {closestMilestone.threshold} {closestMilestone.type.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Weekly Summary */}
      <div className="bg-white/5 border border-white/10 rounded-xl">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#8b5cf6]" />
            <h3 className="text-white font-semibold">This Week</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">{weeklyHosts} events hosted</span>
              <span className="text-white font-medium">{(weeklyHosts * 1.0).toFixed(2)} rewards</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">{weeklyInvites} people invited</span>
              <span className="text-white font-medium">{(weeklyInvites * 0.25).toFixed(2)} rewards</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">{weeklyAttends} events attended</span>
              <span className="text-white font-medium">{(weeklyAttends * 0.10).toFixed(2)}  </span>
            </div>
            <div className="border-t border-white/10 pt-2 mt-2">
              <div className="flex justify-between items-center font-semibold">
                <span className="text-gray-300">Total earned:</span>
                <span className="text-white">{weeklyTotal.toFixed(2)} rewards</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h3 className="text-white font-semibold mb-3">Recent Activity</h3>
        <div className="space-y-2">
          {transactions.length > 0 ? transactions.slice(0, 8).map((transaction) => (
            <div
              key={transaction._id}
              className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10"
            >
              <div className="flex items-center gap-3">
                {getActionIcon(transaction.actionType)}
                <div>
                  <p className="text-white text-sm font-medium">{getActionLabel(transaction)}</p>
                  <p className="text-gray-400 text-xs">{timeAgo(transaction.createdAt)}</p>
                </div>
              </div>
              <span className={`text-sm font-semibold ${transaction.tokensAmount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {transaction.tokensAmount > 0 ? '+' : ''}{transaction.tokensAmount.toFixed(2)}
              </span>
            </div>
          )) : (
            <p className="text-gray-500 text-center py-4">No activity yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
